
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check, ExternalLink, Globe, BookOpen } from 'lucide-react';
import { Source } from '../types';

interface ReportDisplayProps {
  content: string;
  sources: Source[];
  date: string;
}

export const ReportDisplay: React.FC<ReportDisplayProps> = ({ content, sources, date }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden my-8">
      {/* Header Bar */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 text-white px-6 py-6 sm:px-8 flex items-center justify-between shadow-lg">
        <div>
          <h2 className="font-bold text-2xl sm:text-3xl tracking-tight text-white">AI新闻通俗解释</h2>
          <p className="text-blue-100 text-base font-mono mt-1 opacity-90">{date}</p>
        </div>
        <button 
          onClick={handleCopy}
          className="p-2.5 hover:bg-white/10 rounded-xl transition-colors flex items-center gap-2 text-sm font-medium border border-white/20 hover:border-white/40"
          title="复制报告内容"
        >
          {copied ? <Check className="w-5 h-5 text-green-300" /> : <Copy className="w-5 h-5" />}
          <span className="hidden sm:inline">{copied ? '已复制' : '复制全文'}</span>
        </button>
      </div>
      
      {/* Content Area */}
      <div className="p-6 sm:p-10 prose prose-lg prose-slate max-w-none">
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          components={{
            // Main Report Title
            h1: ({node, ...props}) => (
              <h1 className="text-4xl sm:text-5xl font-black text-slate-900 pb-6 mb-10 mt-4 leading-tight tracking-tight border-b-4 border-blue-600/20" {...props} />
            ),
            // Section Headers
            h2: ({node, ...props}) => (
              <div className="mt-16 mb-8 group">
                <h2 className="text-3xl font-bold text-slate-800 flex items-center gap-3" {...props}>
                  <span className="flex-shrink-0 w-2 h-8 bg-blue-600 rounded-full"></span>
                  {props.children}
                </h2>
              </div>
            ),
            h3: ({node, ...props}) => (
              <h3 className="text-2xl font-bold text-slate-800 mt-10 mb-4" {...props} />
            ),
            // Links as Action Buttons (for "Read Original" links inside the text)
            a: ({node, href, children, ...props}) => (
              <a 
                href={href}
                className="inline-flex items-center gap-2 px-5 py-2.5 mt-2 bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white border border-blue-200 hover:border-transparent rounded-full text-sm font-bold transition-all duration-200 no-underline shadow-sm hover:shadow-md transform hover:-translate-y-0.5 group"
                target="_blank"
                rel="noopener noreferrer"
                {...props}
              >
                <Globe className="w-4 h-4" />
                <span>{children}</span>
                <ExternalLink className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 transition-opacity" />
              </a>
            ),
            // Lists
            ul: ({node, ...props}) => <ul className="space-y-4 my-6 list-disc pl-6 marker:text-blue-300" {...props} />,
            li: ({node, ...props}) => (
              <li className="pl-1 text-slate-700 leading-8 text-lg" {...props} />
            ),
            // Highlighted News Titles (Markdown **text**)
            strong: ({node, ...props}) => (
              <strong className="text-xl sm:text-2xl font-extrabold text-blue-900 bg-blue-50/80 px-2 py-0.5 rounded border-b-2 border-blue-200 box-decoration-clone" {...props} />
            ),
            // Paragraphs
            p: ({node, ...props}) => <p className="mb-4 text-slate-600 text-lg leading-8" {...props} />,
            // Blockquotes
            blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-indigo-400 pl-4 py-2 italic text-slate-600 bg-slate-50 rounded-r-lg my-6" {...props} />
          }}
        >
          {content}
        </ReactMarkdown>
      </div>

      {/* Grounding Sources Grid Section */}
      {sources && sources.length > 0 && (
        <div className="bg-slate-50 border-t border-slate-200 p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-4 text-slate-700">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-bold">参考来源 (Grounding Sources)</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {sources.map((source, index) => {
              // Extract domain for cleaner display
              let domain = '';
              try {
                domain = new URL(source.url).hostname.replace('www.', '');
              } catch (e) {
                domain = 'Unknown Source';
              }

              return (
                <a
                  key={index}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col bg-white border border-slate-200 rounded-lg p-3 hover:border-blue-300 hover:shadow-md transition-all duration-200 group h-full no-underline"
                  title={source.title}
                >
                  <span className="text-sm font-medium text-slate-800 line-clamp-2 mb-auto group-hover:text-blue-700">
                    {source.title}
                  </span>
                  <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-50">
                    {/* Try to show favicon if possible, otherwise generic icon */}
                    <img 
                      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`} 
                      alt="" 
                      className="w-3 h-3 opacity-60"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                    <span className="text-xs text-slate-400 truncate w-full">
                      {domain}
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
